"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SectionHeading } from "@/components/ui/section-heading";
import { AppSwiper, SwiperSlide } from "@/components/ui/swiper";
import { useArticles } from "@/hooks/article";
import { extractStrapiMediaUrl } from "@/lib/strapi";
import frame20 from "@/public/assets/frame-20.png";
import frame21 from "@/public/assets/frame-21.png";
import frame22 from "@/public/assets/frame-22.png";

type ArticleItem = {
  id: string;
  title: string;
  category: string;
  imageSrc: string | typeof frame20;
  imageAlt: string;
  slug: string;
};

export function ArticlesSection() {
  const { articles: strapiArticles, isLoading } = useArticles({ pageSize: 3, withBrandFilter: true });

  const fallbackArticles: ArticleItem[] = React.useMemo(
    () => [
      {
        id: "article-1",
        slug: "article-1",
        title: "A Fresh Everyday Use, Romano Eau De Parfum 1",
        category: "Grooming",
        imageSrc: frame20,
        imageAlt: "Romano article cover",
      },
      {
        id: "article-2",
        slug: "article-2",
        title: "A Fresh Everyday Use, Romano Eau De Parfum 2",
        category: "Style",
        imageSrc: frame21,
        imageAlt: "Romano article cover",
      },
      {
        id: "article-3",
        slug: "article-3",
        title: "A Fresh Everyday Use, Romano Eau De Parfum 3",
        category: "Hobby",
        imageSrc: frame22,
        imageAlt: "Romano article cover",
      },
    ],
    [],
  );

  const articles: ArticleItem[] = React.useMemo(() => {
    if (!strapiArticles.length) return fallbackArticles;

    const images = [frame20, frame21, frame22] as const;

    return strapiArticles.map((entity, index) => {
      const attributes = entity.attributes as {
        title?: string | null;
        slug?: string | null;
        image_thumbnail?: unknown;
        image?: unknown;
        article_category?: { name?: string | null; slug?: string | null; LabelSelect?: string | null } | null;
      };

      const slug = attributes.slug ?? String(entity.id);
      const title = attributes.title ?? slug;
      const categoryLabel =
        attributes.article_category?.name ??
        attributes.article_category?.LabelSelect ??
        attributes.article_category?.slug ??
        "Article";

      const imageUrl =
        extractStrapiMediaUrl(attributes.image) ??
        extractStrapiMediaUrl(attributes.image_thumbnail) ??
        images[index % images.length];

      return {
        id: String(entity.id),
        slug,
        title,
        category: categoryLabel,
        imageSrc: imageUrl,
        imageAlt: title,
      };
    });
  }, [fallbackArticles, strapiArticles]);

  return (
    <section id="articles" className="mx-auto w-full px-4 sm:px-6 lg:px-10">
      <SectionHeading eyebrow="News & Articles" title="Latest Articles" />

      {isLoading && strapiArticles.length === 0 ? (
        <div className="mx-auto mt-8 max-w-6xl animate-pulse sm:mt-10">
          <div className="grid gap-6 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="overflow-hidden rounded-3xl">
                <div className="aspect-4/3 w-full rounded-3xl bg-muted" />
                <div className="px-4 pt-4 pb-2 sm:px-6">
                  <div className="mx-auto h-3 w-24 rounded-lg bg-muted" />
                  <div className="mx-auto mt-3 h-4 w-[min(260px,70%)] rounded-lg bg-muted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <AppSwiper
          className="mx-auto mt-8 max-w-6xl sm:mt-10"
          swiperClassName="pb-6"
          showNavigation={false}
          slidesPerView={1.2}
          spaceBetween={24}
          centeredSlides={false}
          watchOverflow
          breakpoints={{
            640: { slidesPerView: 1.8, spaceBetween: 24 },
            768: { slidesPerView: 3, spaceBetween: 32 },
          }}
        >
          {articles.map((article) => (
            <SwiperSlide key={article.slug} className="h-auto p-1">
              <Link
                href={`/articles/${article.slug}`}
                className="block focus:outline-none"
              >
                <Card className="overflow-hidden rounded-3xl border-none bg-transparent shadow-none transition hover:opacity-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                  <div className="relative aspect-4/3 w-full overflow-hidden rounded-3xl bg-muted">
                    <Image
                      src={article.imageSrc}
                      alt={article.imageAlt}
                      fill
                      className="object-cover"
                      sizes="(min-width: 768px) 360px, (min-width: 640px) 60vw, 85vw"
                      draggable={false}
                      placeholder={typeof article.imageSrc === "string" ? "empty" : "blur"}
                      unoptimized={typeof article.imageSrc === "string" && /^https?:\/\//i.test(article.imageSrc)}
                    />
                  </div>

                  <div className="px-4 pt-4 pb-2 sm:px-6">
                    <p className="text-center text-[clamp(11px,0.95vw,13px)] tracking-[0.18em] text-[#868686]">
                      {article.category}
                    </p>
                    <p className="mx-auto mt-1 max-w-[26ch] text-center text-[clamp(14px,1.4vw,18px)] font-medium leading-snug tracking-tight text-foreground">
                      {article.title}
                    </p>
                  </div>
                </Card>
              </Link>
            </SwiperSlide>
          ))}
        </AppSwiper>
      )}

      <div className="mt-4 flex justify-center">
        <Button
          asChild
          className="rounded-full bg-primary px-8 text-[clamp(11px,0.95vw,13px)] font-semibold tracking-[0.18em] text-white hover:bg-primary/90"
        >
          <Link href="/articles">VIEW ALL</Link>
        </Button>
      </div>
    </section>
  );
}
